<template>
  <div>
    <v-dialog v-model="visible" persistent max-width="640" content-class="green-coffee-modal" @click:outside="close">
      <v-card>
        <v-form ref="mainForm" lazy-validation>
        <v-toolbar color="primary" dark flat dense>
          <v-toolbar-title class="font-weight-bold">{{ $t('dashboardSection.greenCoffee') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-4 pb-2">
          <v-row dense>
            <v-col cols="12" sm="6">
              <label class="text-caption font-weight-medium">{{ $t('memberDataSection.greenCoffee.selectProductType')
                }}</label>
              <v-select v-model="form.productTypeId" :items="productTypes" item-text="name" item-value="id" outlined
                dense hide-details :placeholder="$t('memberDataSection.pOrder.makeSelection')"></v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="text-caption font-weight-medium">{{ $t('memberDataSection.greenCoffee.processDate')
                }}</label>
              <v-menu v-model="processDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y
                min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="form.processDate" outlined dense hide-details readonly v-bind="attrs" v-on="on"
                    append-icon="mdi-calendar"></v-text-field>
                </template>
                <v-date-picker v-model="form.processDate" no-title scrollable
                  @input="processDateMenu = false"></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12">
              <label class="text-caption font-weight-medium">{{
                $t('memberDataSection.greenCoffee.selectProcessingBatch') }}
                <span class="error--text">*</span>
              </label>
              <v-select v-model="form.processingBatchId" :items="processingBatches" item-text="display" item-value="id"
                outlined dense :rules="[v => !!v || this.$t('memberDataSection.greenCoffee.processingBatchRequired')]"
                :placeholder="$t('memberDataSection.pOrder.makeSelection')"
                @change="onBatchSelect"></v-select>
            </v-col>
          </v-row>

          <template v-if="selectedBatch">
            <v-divider class="my-3"></v-divider>
            <v-row dense>
              <v-col cols="12" sm="4">
                <label class="text-caption font-weight-medium">{{ $t('memberDataSection.greenCoffee.lotIdAndDate')
                  }}</label>
                <p class="text-body-2 mb-0">{{ selectedBatch.lotId || '—' }}</p>
                <p class="text-body-2 mb-0">{{ selectedBatch.endDate || '—' }}</p>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="text-caption font-weight-medium">{{ $t('memberDataSection.greenCoffee.typeAndQuantity')
                  }}</label>
                <p class="text-body-2 mb-0">{{ selectedBatch.typeInfo || '—' }}</p>
                <p class="text-body-2 mb-0">{{ selectedBatch.processInfo || '—' }}</p>
                <p class="text-body-2 mb-0">{{ selectedBatch.quantity ? selectedBatch.quantity + ' kg' : '—' }}</p>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="text-caption font-weight-medium">{{ $t('memberDataSection.greenCoffee.supplier')
                  }}</label>
                <p class="text-body-2 mb-0">{{ selectedBatch.supplier || '—' }}</p>
              </v-col>
            </v-row>
          </template>

          <v-row dense class="mt-2">
            <v-col v-if="isManualGreenBeansLotId" cols="12" sm="6">
              <label class="text-caption font-weight-medium">{{ $t('memberDataSection.greenCoffee.outturnNumber') }}</label>
              <v-text-field
                v-model="form.outturnNumber"
                outlined
                dense
                :loading="outturnChecking"
                :error-messages="outturnError"
                :success-messages="outturnSuccess"
                :placeholder="$t('memberDataSection.greenCoffee.enterHere')"
                @input="onOutturnInput"
              ></v-text-field>
            </v-col>
            <v-col cols="12" :sm="isManualGreenBeansLotId ? 6 : 12">
              <label class="text-caption font-weight-medium">{{ $t('memberDataSection.greenCoffee.totalInputQuantity')
                }}</label>
              <v-text-field
                ref="totalInputQuantityField"
                v-model.number="form.totalInputQuantity"
                type="number"
                outlined
                dense
                hide-details="auto"
                :rules="[validateTotalInputQuantity]"
                :placeholder="$t('memberDataSection.greenCoffee.enterHere')"
                @input="onTotalInputQuantityInput"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-divider class="my-3"></v-divider>

          <h3 class="text-subtitle-1 font-weight-bold brown--text text--darken-2 my-4">{{
            $t('memberDataSection.greenCoffee.qualityControl') }}</h3>
          <v-row dense>
            <v-col cols="12" sm="6">
              <label class="text-caption font-weight-medium">{{ $t('memberDataSection.greenCoffee.airHumidity')
                }}</label>
              <v-text-field v-model="form.airHumidity" type="number" outlined dense hide-details
                :placeholder="$t('memberDataSection.greenCoffee.enterHere')"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="text-caption font-weight-medium">{{ $t('memberDataSection.greenCoffee.density') }}</label>
              <v-text-field v-model="form.density" type="number" outlined dense hide-details
                :placeholder="$t('memberDataSection.greenCoffee.enterHere')"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="text-caption font-weight-medium">{{
                $t('memberDataSection.greenCoffee.greenCoffeeQualityScore')
                }}</label>
              <v-select v-model="form.greenCoffeeQualityScoreId" :items="qualityScores" item-text="name" item-value="id"
                outlined dense hide-details :placeholder="$t('memberDataSection.pOrder.makeSelection')"></v-select>
            </v-col>
          </v-row>
          
          <v-divider class="my-6"></v-divider>

          <h3 class="text-subtitle-1 font-weight-bold brown--text text--darken-2 my-4">{{
            $t('memberDataSection.greenCoffee.batchProduction') }}</h3>
          <v-row dense>
            <v-col cols="12" sm="6">
              <label class="text-caption font-weight-medium">{{ $t('memberDataSection.greenCoffee.unsortedGreenBeans')
                }}</label>
              <v-text-field v-model="form.unsortedGreenBeans" type="number" outlined dense hide-details
                :placeholder="$t('memberDataSection.greenCoffee.enterHere')"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="text-caption font-weight-medium">{{
                $t('memberDataSection.greenCoffee.batchProductionDensity')
                }}</label>
              <v-text-field v-model="form.batchProductionDensity" type="number" outlined dense hide-details
                :placeholder="$t('memberDataSection.greenCoffee.enterHere')"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="text-caption font-weight-medium">{{
                $t('memberDataSection.greenCoffee.percentagePrimaryDefect')
                }}</label>
              <v-text-field v-model="form.percentagePrimaryDefect" type="number" outlined dense hide-details
                :placeholder="$t('memberDataSection.greenCoffee.enterHere')"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="text-caption font-weight-medium">{{
                $t('memberDataSection.greenCoffee.percentageSecondaryDefect')
                }}</label>
              <v-text-field v-model="form.percentageSecondaryDefect" type="number" outlined dense hide-details
                :placeholder="$t('memberDataSection.greenCoffee.enterHere')"></v-text-field>
            </v-col>
          </v-row>

          <v-divider class="my-6"></v-divider>

        
          <h3 class="text-subtitle-1 font-weight-bold brown--text text--darken-2 my-4">{{
            $t('memberDataSection.greenCoffee.greenBeans') }}</h3>
          <v-row dense>
            <v-col cols="12" sm="6">
              <label class="text-caption font-weight-medium">{{ $t('memberDataSection.greenCoffee.totalGreenBeans') }}
                <span class="error--text">*</span>
              </label>
              <v-text-field v-model.number="form.totalGreenBeans" type="number" outlined dense
                :rules="[v => (v !== null && v !== '' && Number(v) > 0) || this.$t('memberDataSection.greenCoffee.totalGreenBeansRequired')]"
                @input="computeBags"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="text-caption font-weight-medium">{{ $t('memberDataSection.greenCoffee.unitSize') }}</label>
              <v-text-field v-model.number="form.unitSize" type="number" outlined dense hide-details
                @input="computeBags"></v-text-field>
            </v-col>
            <v-col cols="12">
              <p class="text-body-2 mb-0">{{ $t('memberDataSection.greenCoffee.totalBags') }}: {{ totalBags }}</p>
              <p class="text-body-2 my-2">{{ $t('memberDataSection.greenCoffee.leftoverQuantity') }}: {{ leftoverQuantity }} kg</p>
              <p class="text-caption text--secondary">{{ $t('memberDataSection.greenCoffee.leftoverQuantityDesc') }}</p>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="text-caption font-weight-medium">{{ $t('memberDataSection.greenCoffee.expiryDate')
                }}</label>
              <v-menu v-model="expiryDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y
                min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="form.expiryDate" outlined dense hide-details readonly v-bind="attrs" v-on="on"
                    append-icon="mdi-calendar"></v-text-field>
                </template>
                <v-date-picker v-model="form.expiryDate" no-title scrollable
                  @input="expiryDateMenu = false"></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="text-caption font-weight-medium">{{ $t('memberDataSection.greenCoffee.label') }}</label>
              <v-select v-model="form.labelId" :items="labels" item-text="title" item-value="id" outlined dense
                hide-details :placeholder="$t('memberDataSection.pOrder.makeSelection')"></v-select>
            </v-col>
          </v-row>
          <v-card-actions class="px-0 pt-4 pb-0">
            <v-btn outlined color="primary" class="text-none" @click="openQualityGradingModal">
              <v-icon left small>mdi-ribbon</v-icon>
              {{ $t('memberDataSection.greenCoffee.qualityControlButton') }}
            </v-btn>
          </v-card-actions>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn outlined color="primary" @click="close">{{ $t('cancel') }}</v-btn>
          <v-btn color="primary" class="white--text" @click="submit">{{ $t('submit') }}</v-btn>
        </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <GreenBeansQualityGradingModal v-model="qualityGradingModalVisible" :outturn-number="displayOutturnNumber"
      :total-quantity="form.totalGreenBeans" :unit-size="form.unitSize" :lots="qualityGradingLots"
      :quality-scores="qualityScores" :labels="labels" :grade-options="gradeOptions"
      @finish-grading="onQualityGradingFinished" />
  </div>
</template>

<script>
import moment from "moment";
import GreenCoffeeService from "@/_services/GreenCoffeeService";
import GreenBeansQualityGradingModal from "./GreenBeansQualityGradingModal.vue";
import LoadingMixin from "@/mixins/LoadingMixin";
import { isKenyaClient } from "@/utils";

export default {
  name: "CreateGreenCoffeeModal",
  components: { GreenBeansQualityGradingModal },
  mixins: [LoadingMixin],
  props: {
    value: { type: Boolean, default: false },
    addedByName: { type: String, default: "" },
  },
  data() {
    return {
      visible: this.value,
      qualityGradingModalVisible: false,
      qualityGradingLots: [],
      processDateMenu: false,
      expiryDateMenu: false,
      outturnChecking: false,
      outturnError: "",
      outturnSuccess: "",
      outturnDebounceTimer: null,
      greenBeansLotIdType: "system",
      form: {
        productTypeId: null,
        processDate: moment().format("YYYY-MM-DD"),
        processingBatchId: null,
        outturnNumber: "",
        totalInputQuantity: "",
        airHumidity: "",
        density: "",
        greenCoffeeQualityScoreId: null,
        unsortedGreenBeans: "",
        batchProductionDensity: "",
        percentagePrimaryDefect: "",
        percentageSecondaryDefect: "",
        totalGreenBeans: null,
        unitSize: isKenyaClient() ? 60 : null,
        expiryDate: moment().format("YYYY-MM-DD"),
        labelId: null,
      },
      productTypes: [],
      processingBatches: [],
      qualityScores: [],
      labels: [],
      gradeOptions: [],
      selectedBatch: null,
    };
  },
  computed: {
    isNaccuUser() {
      return isKenyaClient();
    },
    selectedBatchQuantity() {
      const q = Number(this.selectedBatch?.quantity);
      return Number.isFinite(q) ? q : null;
    },
    totalBags() {
      const total = Number(this.form.totalGreenBeans) || 0;
      const unit = Number(this.form.unitSize) || 1;
      return unit ? Math.floor(total / unit) : 0;
    },
    leftoverQuantity() {
      const total = Number(this.form.totalGreenBeans) || 0;
      const unit = Number(this.form.unitSize) || 1;
      return unit ? total % unit : total;
    },
    isManualGreenBeansLotId() {
      return this.greenBeansLotIdType === "manual";
    },
    displayOutturnNumber() {
      return this.isManualGreenBeansLotId ? (this.form.outturnNumber || "") : "";
    },
  },
  watch: {
    value(v) {
      this.visible = v;
      if (v) {
        this.resetForm();
        this.loadOptions();
      }
    },
    visible(v) {
      this.$emit("input", v);
    },
  },
  mounted() {
    this.loadOptions();
  },
  methods: {
    async loadOptions() {
      this.startLoading();
      this.productTypes = [];
      this.processingBatches = [];
      this.qualityScores = [];
      this.labels = [];
      this.gradeOptions = [];
      try {
        const res = await GreenCoffeeService.getGreenCoffeeOptions();
        if (res?.data) {
          const d = res.data;
          this.productTypes = d.productTypes || [];
          this.processingBatches = d.processingBatches || [];
          this.qualityScores = d.greenCoffeeQuality || [];
          this.labels = d.labels || [];
          this.gradeOptions = d.gradeOptions || [];
          const lotType = d.greenBeansLotTypeSetting;
          this.greenBeansLotIdType =
            lotType === "manual" || lotType === "system" ? lotType : "system";
        } else {
          this.productTypes = [];
          this.processingBatches = [];
          this.qualityScores = [];
          this.labels = [];
          this.gradeOptions = [];
          this.greenBeansLotIdType = "system";
        }
      } catch (_) {
        this.productTypes = [];
        this.processingBatches = [];
        this.qualityScores = [];
        this.labels = [];
        this.gradeOptions = [];
        this.greenBeansLotIdType = "system";
      } finally {
        this.stopLoading();
      }
    },
    onBatchSelect() {
      const batch = this.processingBatches.find((b) => b.id === this.form.processingBatchId);
      this.selectedBatch = batch || null;
      if (batch) {
        const parchmentOut = batch.parchmentOut ?? batch.quantity;
        this.form.totalInputQuantity =
          parchmentOut != null && parchmentOut !== "" ? String(parchmentOut) : "";
      } else {
        this.form.totalInputQuantity = "";
      }
      this.onTotalInputQuantityInput();
    },
    computeBags() { },
    onTotalInputQuantityInput() {
      this.$nextTick(() => {
        if (this.$refs.totalInputQuantityField?.validate) {
          this.$refs.totalInputQuantityField.validate();
        }
      });
    },
    validateTotalInputQuantity(v) {
      if (v == null || v === "") return true;
      const n = Number(v);
      if (!Number.isFinite(n)) return true;
      if (n < 0) return true;

      const max = this.selectedBatchQuantity;
      if (max == null) return true;
      return (
        n <= max || "The weight of Green Coffee producedd is more than batch processed"
      );
    },
    openQualityGradingModal() {
      this.qualityGradingModalVisible = true;
    },
    onQualityGradingFinished(lots) {
      this.qualityGradingLots = lots || [];
      this.qualityGradingModalVisible = false;
    },
    resetForm() {
      this.form.productTypeId = null;
      this.form.processDate = moment().format("YYYY-MM-DD");
      this.form.expiryDate = moment().format("YYYY-MM-DD");
      this.form.processingBatchId = null;
      this.selectedBatch = null;
      this.form.outturnNumber = "";
      this.form.totalInputQuantity = "";
      this.form.airHumidity = "";
      this.form.density = "";
      this.form.greenCoffeeQualityScoreId = null;
      this.form.unsortedGreenBeans = "";
      this.form.batchProductionDensity = "";
      this.form.percentagePrimaryDefect = "";
      this.form.percentageSecondaryDefect = "";
      this.form.totalGreenBeans = null;
      this.form.unitSize = this.isNaccuUser ? 60 : null;
      this.form.labelId = null;
      this.qualityGradingLots = [];
      this.outturnError = "";
      this.outturnSuccess = "";
      this.outturnChecking = false;
      if (this.outturnDebounceTimer) clearTimeout(this.outturnDebounceTimer);
      if (this.$refs.mainForm) this.$refs.mainForm.resetValidation();
    },
    onOutturnInput(val) {
      this.outturnError = "";
      this.outturnSuccess = "";
      if (this.outturnDebounceTimer) clearTimeout(this.outturnDebounceTimer);
      if (!val || !val.trim()) return;
      this.outturnDebounceTimer = setTimeout(async () => {
        this.outturnChecking = true;
        try {
          const res = await GreenCoffeeService.checkOutturnNumber(val.trim());
          if (res?.data?.available) {
            this.outturnSuccess = this.$t('memberDataSection.greenCoffee.outturnAvailable');
            this.outturnError = "";
          } else {
            this.outturnError = this.$t('memberDataSection.greenCoffee.outturnExists');
            this.outturnSuccess = "";
          }
        } catch (_) {
          this.outturnError = "";
          this.outturnSuccess = "";
        } finally {
          this.outturnChecking = false;
        }
      }, 600);
    },
    close() {
      this.visible = false;
      this.$emit("close");
    },
    async submit() {
      if (!this.$refs.mainForm.validate()) return;
      if (this.isManualGreenBeansLotId && this.outturnError) return;
      this.startLoading();
      try {
        const selectedLabel = this.labels.find((l) => l.id === this.form.labelId);
        const selectedQualityScore = this.qualityScores.find((q) => q.id === this.form.greenCoffeeQualityScoreId);

        const qualityGradings = this.qualityGradingLots.map((lot) => ({
          qualityTitle: lot.qualityTitle || lot.qualityScore?.name || String(lot.qualityScore || ""),
          quantity: lot.quantity,
          quantityUnit: "kg",
          qualityScore: lot.qualityScore || "",
          label: (lot.label && typeof lot.label === "object" && lot.label.id != null) ? { id: lot.label.id } : (lot.labelId != null ? { id: lot.labelId } : lot.label || ""),
          unitSize: String(lot.unitSize || ""),
          unitSizeUnit: "kg",
          pocketQuantity: lot.totalBags || 0,
          lotType: this.isNaccuUser ? "manual" : "system",
        }));

        const totalGreenBeans = Number(this.form.totalGreenBeans) || 0;
        const lotsTotal = qualityGradings.reduce(
          (sum, g) => sum + (Number(g.quantity) || 0),
          0
        );

        if (totalGreenBeans > 0 && lotsTotal > totalGreenBeans) {
          this.$notify({
            title: this.$t('memberDataSection.greenCoffee.totalGreenBeansExceeded'),
            type: "error",
          });
          this.stopLoading();
          return;
        }

        const payload = {
          productTypeId: this.form.productTypeId || null,
          purchaseDate: this.form.processDate,
          buyingStationParchmentId: this.form.processingBatchId || null,
          parchmentChecking: this.form.totalInputQuantity || "0",
          parchmentCheckingUnitId: 1,
          qualityControlHumidity: this.form.airHumidity || null,
          qualityControlDensity: this.form.density || null,
          parchmentQualityScore: selectedQualityScore?.name || null,
          parchmentQualityScoreId: this.form.greenCoffeeQualityScoreId || null,
          batchProductionKilogramAsalan: this.form.unsortedGreenBeans || null,
          batchProductionDensity: this.form.batchProductionDensity || null,
          batchProductionPrimaryDefect: this.form.percentagePrimaryDefect || null,
          batchProductionSecondaryDefect: this.form.percentageSecondaryDefect || null,
          greenBeansTotal: totalGreenBeans || null,
          greenBeansBags: this.totalBags || null,
          greenBeansExpiry: this.form.expiryDate || null,
          greenBeansLabel: selectedLabel ? { id: selectedLabel.id } : null,
          qualityGradings: qualityGradings.length ? qualityGradings : undefined,
          totalWaste:
            totalGreenBeans > 0 && lotsTotal >= 0
              ? Math.max(totalGreenBeans - lotsTotal, 0)
              : null,
          lotType: this.isManualGreenBeansLotId ? "manual" : "system",
          outturnNumber: this.isManualGreenBeansLotId
            ? (this.form.outturnNumber || undefined)
            : undefined,
          buyingStationParchments:
            this.selectedBatch && this.selectedBatch.batchCode
              ? [
                  {
                    // Must match BuyingStationProcessingBatch.batchCode to link parchment to batch
                    buyingStationParchmentId: this.selectedBatch.batchCode,
                    quantity: this.form.totalInputQuantity || 0,
                  },
                ]
              : [],
        };

        await GreenCoffeeService.createAdminGreenCoffee(payload);
        this.$emit("submitted");
        this.close();
      } catch (err) {
        console.error("Failed to create green coffee:", err);
        this.$emit("error", err);
      } finally {
        this.stopLoading();
      }
    },
  },
};
</script>

<style scoped>
.brown--text.text--darken-2 {
  color: #5d4037 !important;
}
</style>
